<template>
  <v-dialog v-model="dialog" max-width="1240px">
    <v-card>
      <form>
        <v-card-title class="pb-0">
          <v-container style="max-width: 100%" class="pt-0 pb-0">
            <v-row>
              <v-col>
                <p class="headline mb-0" style="width: 100%">
                  {{ mess.dialog_create_lead_articles_title }}
                </p>
              </v-col>
            </v-row>
          </v-container>
        </v-card-title>

        <v-card-text>
          <v-container class="pt-0" style="max-width: 100%">
            <v-row>
              <v-col>
                <article-legend />

                <market-area-filter
                  v-if="marketAreaFilters"
                  :filters="marketAreaFilters"
                  :active-filters="marketArea"
                  @change-filter="onChangeMarketAreaFilter"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" style="height: 48vh; overflow-y: scroll">
                <v-data-table
                  :headers="articleHeaders"
                  :items="articles"
                  v-model="selected"
                  :show-select="!!(articles && articles.length)"
                  :single-select="false"
                  item-key="index"
                  group-by="equipment_name"
                  group-expanded
                  hide-default-footer
                  disable-pagination
                  class="create-lead-articles-table"
                >
                  <template v-slot:header.price="{ header }">
                    <div class="text-no-wrap">
                      <span>{{ header.text }}</span>

                      <v-tooltip right content-class="table-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon color="primary" dark v-bind="attrs" v-on="on">
                            mdi-alert-circle-outline
                          </v-icon>
                        </template>

                        <span>{{ mess["price_tooltip"] }}</span>
                      </v-tooltip>
                    </div>
                  </template>

                  <template v-slot:group.header="{ group, toggle }">
                    <td
                      @click="toggle(group)"
                      colspan="12"
                      style="cursor: pointer"
                    >
                      <b style="width: 100%; font-size: 14px">
                        <v-icon size="26">mdi-chevron-down</v-icon>
                        {{ group }}
                      </b>
                    </td>
                  </template>

                  <template v-slot:item.name="{ item }">
                    <a class="" :href="item.link" target="_blank">
                      {{ item.name }}
                    </a>
                  </template>

                  <template v-slot:item.code="{ item }">
                    <div style="max-width: 150px">{{ item.code }}</div>
                  </template>

                  <template v-slot:item.oem="{ item }">
                    <div style="max-width: 150px">{{ item.oem }}</div>
                  </template>

                  <template v-slot:item.icons="{ item }">
                    <article-icons
                      :is-kit="item.isKit"
                      :is-analog="item.isAnalog"
                      :have-on-storage="item.haveOnStorage"
                      :have-analog-on-storage="item.haveAnalogOnStorage"
                      :in-transit="item.inTransit"
                      :sales-draw="item.salesDraw"
                    />
                  </template>

                  <template v-slot:item.available="{ item }">
                    <available-list
                      :loading="availableListLoading"
                      :available="item.available"
                      :transit="item.transit"
                      :stores="item.market_area_items"
                      :marketArea="marketArea"
                    />
                  </template>

                  <template v-slot:item.shippingEstimate="{ item }">
                    <b
                      style="white-space: nowrap;"
                      :style="{ color: siteColors.primary }"
                    >
                      <shipping-estimate
                        :item="item"
                        :market-area="marketArea"
                      />
                    </b>
                  </template>

                  <template v-slot:item.price="{ item }">
                    <span style="white-space: nowrap">{{ item.price }}</span>
                  </template>

                  <template v-slot:item.second_price="{ item }">
                    <span
                      v-if="priceType.isSecondPrice"
                      style="white-space: nowrap"
                    >
                      {{ item["second_price"] }}
                    </span>
                  </template>

                  <template v-slot:item.qty="{ item }">
                    <button-counter-deprecated :item="item" />
                  </template>

                  <template v-slot:item.remove="{ item }">
                    <div class="d-flex">
                      <v-btn
                        color="primary"
                        :title="mess.dialog_base_add_to_cart"
                        icon
                        @click="addToCartSingleItem(item)"
                      >
                        <v-icon size="20">
                          mdi-cart-plus
                        </v-icon>
                      </v-btn>

                      <v-btn
                        color="primary"
                        :title="mess.dialog_base_button_remove"
                        icon
                        @click="removeArticle(item)"
                      >
                        <v-icon size="20">
                          mdi-delete-forever-outline
                        </v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions style="padding: 0 24px 16px">
          <v-btn
            class="mb-3"
            style="text-transform: unset; font-weight: 700; letter-spacing: 0"
            outlined
            color="primary"
            height="40px !important"
            depressed
            @click="generateXlsx"
          >
            {{ mess.dialog_base_button_download_xlsxl }}
          </v-btn>

          <v-btn
            class="mb-3"
            style="text-transform: unset; font-weight: 700; letter-spacing: 0"
            outlined
            color="primary"
            height="40px !important"
            depressed
            :disabled="loading"
            @click="addToCart(articles)"
          >
            {{ mess.dialog_base_button_add_all_to_cart }} ({{
              articles.length
            }})
          </v-btn>

          <v-btn
            class="mb-3"
            style="text-transform: unset; font-weight: 700; letter-spacing: 0"
            :disabled="selected.length === 0 || loading"
            outlined
            color="primary"
            height="40px !important"
            depressed
            @click="addToCart(selected)"
          >
            {{ mess.dialog_base_button_add_selected_to_cart }} ({{
              selected.length
            }})
          </v-btn>

          <v-combobox
            v-model="generateXlsxFormat"
            :items="xlsxFormat"
            class="mt-3 ml-3"
            color="primary"
            outlined
            :dense="true"
            height="40px"
            item-text="pdm_elementid"
            :label="mess['dialog_base_combobox_choose_generate_xlsx_format']"
          />
        </v-card-actions>

        <v-btn absolute top right text icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </form>

      <v-overlay :value="overlay" absolute color="white" opacity="1">
        <v-progress-circular indeterminate size="64" color="primary" />
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
// Components
import ButtonCounterDeprecated from "@/components/ButtonCounterDeprecated";
import ArticleLegend from "@/components/ArticleLegend";
import ArticleIcons from "@/components/ArticleIcons";
import AvailableList from "@/components/AvailableList/AvailableList";
import MarketAreaFilter from "@/components/MarketAreaFilter/MarketAreaFilter";
import ShippingEstimate from "@/components/ShippingEstimate";

// Services
import { cartService } from "@/services/cart-service/cart-service";

// Utils
import cloneDeep from "lodash/cloneDeep";
import notifications from "@/models/notificationTemplates";
import Xlsx from "@/utils/create-xlsx-data";

export default {
  name: "CreateLeadArticlesDialog",
  components: {
    ArticleLegend,
    ArticleIcons,
    ButtonCounterDeprecated,
    AvailableList,
    MarketAreaFilter,
    ShippingEstimate
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    sm: {
      type: Boolean,
      default: false
    },
    orders: {
      type: Array,
      default: () => []
    },
    exportFields: {
      type: Array,
      default: () => []
    },
    articleHeaders: {
      type: Array,
      default: () => []
    },
    company: {
      type: String,
      default: ""
    },
    marketAreaFilters: {
      type: [Array, null],
      default: () => null
    }
  },
  data() {
    return {
      dialog: false,
      overlay: true,
      loading: false,
      articles: [],
      selected: [],
      marketArea: [],
      availableListLoading: false,
      headers: [
        {
          header: "Наименование",
          key: "name"
        },
        {
          header: "Артикул",
          key: "code"
        },
        {
          header: "ОЕМ Артикул",
          key: "oem"
        },
        {
          header: "Наличие",
          key: "available"
        },
        {
          header: "Цена",
          key: "price"
        }
      ],
      generateXlsxFormat: null
    };
  },
  computed: {
    xlsxFormat() {
      return [
        this.mess.dialog_base_combobox_xlsx_simple,
        this.mess.dialog_base_combobox_xlsx_splitted
      ];
    }
  },
  methods: {
    onChangeMarketAreaFilter(evt) {
      if (this.marketArea.includes(evt)) {
        if (this.marketArea.length === 1) return;
        this.availableListLoading = true;
        this.marketArea.splice(this.marketArea.indexOf(evt), 1);
      } else {
        this.availableListLoading = true;
        this.marketArea.push(evt);
      }

      setTimeout(() => {
        this.availableListLoading = false;
      }, 250);
    },
    setInitialMarketArea() {
      this.marketArea = cloneDeep(this.marketAreaFilters || []);
    },
    addToCartSingleItem(item) {
      cartService
        .addToCart(item)
        .then(data => {
          if (data.success) {
            this.$emit(
              "added-to-cart-single-item",
              notifications.success.addToCart
            );
          } else {
            this.$emit("error", notifications.error.base);
          }
        })
        .catch(() => {
          this.$emit("error", notifications.error.base);
        });
    },
    addToCart(items) {
      this.loading = true;
      cartService
        .addArrayItemsToCart(items)
        .then(data => {
          if (data.success) {
            this.$emit("added-to-cart");
          } else {
            throw new Error();
          }
        })
        .catch(err => {
          throw err;
        })
        .finally(() => (this.loading = false));
    },
    indexingArticles(articles) {
      return articles.map((article, i) => ({ ...article, index: i }));
    },
    removeArticle(item) {
      this.articles.splice(this.articles.indexOf(item), 1);

      const indexInSelected = this.selected.indexOf(item);
      if (indexInSelected !== -1) this.selected.splice(indexInSelected, 1);
    },
    resetForm() {
      this.overlay = true;
      this.resetData();
    },
    resetData() {
      this.articles = [];
      this.selected = [];
    },
    async generateXlsx() {
      const xlsx = new Xlsx(this.headers, this.articles);
      const worksheet =
        this.generateXlsxFormat === this.xlsxFormat[1]
          ? xlsx.createSeparatedByNameSheet("equipment_name")
          : xlsx.createSimpleSheet();

      await xlsx.generateXlsx(this.company, worksheet);
    }
  },
  watch: {
    isVisible(val) {
      this.dialog = val;
    },
    dialog(val) {
      this.$emit("update:is-visible", val);
      !!val || this.resetForm();
    },
    orders(val) {
      this.articles = this.indexingArticles(val);
      if (this.dialog) this.overlay = false;
    }
  },
  created() {
    this.setInitialMarketArea();
  }
};
</script>

<style lang="scss">
.create-lead-articles-table.v-data-table {
  thead tr {
    vertical-align: baseline;
    line-height: 1;
  }

  th {
    padding-top: 8px;
    padding-bottom: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.4;
    color: #9c9c9c;
  }

  td {
    font-size: 12px;
    color: #000000;
  }
}
</style>
